import React, { useState, useEffect, useMemo } from 'react';
import { useAtom } from 'jotai';
import useTranslate from '../../Hooks/useTranslate';
import { Typography, Paper, IconButton, Collapse, Box } from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { SchemaRenderer } from './SchemaRenderer';
import { FlexBox, ColumnFlexBox } from '../FlexBox';
import { makeGenericFieldAtom } from '../../store/genericFormStore';
import Switch from './Switch';

const SchemaPaper = function SchemaPaper({
  name,
  description,
  active,
  dynamicPath,
  uiSchemaConfiguration,
  onToggle,
  sectionId,
  waveFilterId,
}) {
  const translate = useTranslate();

  const [collapsed, setCollapsed] = useState(false);

  // The active state lives in the generic form payload at `${dynamicPath}.active`
  // so the toggle persists into the form (and is read back by SchemaWrapper).
  const activeAtom = useMemo(
    () => makeGenericFieldAtom(`${dynamicPath}.active`),
    [dynamicPath]
  );
  const [isActive, setIsActive] = useAtom(activeAtom);

  // Seed the store with the initial (API) value if nothing has been set yet.
  // `isInit` keeps this silent so it doesn't mark the form as changed.
  useEffect(() => {
    if (isActive === undefined) {
      setIsActive(!!active, { isInit: true });
    }
  }, [isActive, active, setIsActive]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => !prev);
  };

  const handleToggle = () => {
    const nextActive = !isActive;

    // Persist server-side first; only flip the form state once the API confirms.
    if (onToggle) {
      onToggle(sectionId, waveFilterId, () => {
        setIsActive(nextActive);
      });
    } else {
      setIsActive(nextActive);
    }
  };

  const isInactive = !isActive;

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        padding: '16px 24px',
        width: '100%',
        opacity: isInactive ? 0.5 : 1,
        transition: 'opacity 0.2s ease, background-color 0.2s ease',
      })}
    >
      <FlexBox alignItems="center" gap="16px" marginBottom="8px">
        <ColumnFlexBox flexGrow={1}>
          {name && (
            <Typography variant="h6" fontWeight="bold">
              {translate(name)}
            </Typography>
          )}
          {description && (
            <Typography variant="subtitle1" color="text.secondary">
              {translate(description)}
            </Typography>
          )}
        </ColumnFlexBox>
        <Switch
          checked={!!isActive}
          onChange={handleToggle}
          label={isActive ? translate('ACTIVE') : translate('INACTIVE')}
          labelPlacement="start"
          slotProps={{
            typography: {
              sx: {
                textTransform: 'uppercase',
                fontWeight: 'bold',
                color: isInactive ? 'text.disabled' : 'primary.main',
                fontSize: '0.9rem',
              },
            },
          }}
        />
        <IconButton
          id="toggle-paper"
          onClick={toggleCollapsed}
          disabled={isInactive}
        >
          <KeyboardArrowDownRoundedIcon
            sx={{
              transform: !collapsed ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.3s ease',
            }}
          />
        </IconButton>
      </FlexBox>
      <Collapse in={!collapsed}>
        <Box sx={{ pointerEvents: isInactive ? 'none' : 'auto' }}>
          <SchemaRenderer
            key={dynamicPath}
            basePath={dynamicPath}
            fields={uiSchemaConfiguration ?? []}
          />
        </Box>
      </Collapse>
    </Paper>
  );
};

export default SchemaPaper;
